import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Import components
import FilterBar from '../components/cat/FilterBar';
import NPSScore from '../components/cat/NPSScore';
import SentimentOverview from '../components/cat/SentimentOverview';
import CategoryBreakdown from '../components/cat/CategoryBreakdown';
import SubcategoryAnalysis from '../components/cat/SubcategoryAnalysis';
import EntityAnalysis from '../components/cat/EntityAnalysis';
import AIInsights from '../components/cat/AIInsights';
import TopPainPoints from '../components/cat/TopPainPoints';
// import FeedbackTable from '../components/cat/FeedbackTable';
import SentimentByCategory from '../components/cat/SentimentByCategory';
import CategoryInsights from '../components/cat/CategoryInsights';
import { useData } from '../contexts/DataContext';

const Dashboard = () => {
  const { data: bankData } = useData(); // Get data from context
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [customerType, setCustomerType] = useState('all'); // New state for customer type filter

  // Process data when bankData is available or when customerType filter changes
  useEffect(() => {
    const processData = async () => {
      if (!bankData) return;
      
      try {
        setLoading(true);
        
        // Filter data by customerType if a specific type is selected
        let filteredBankData = bankData.data;
        if (customerType !== 'all') {
          filteredBankData = bankData.data.filter(item => item.customerType === customerType);
        }
        
        // Process data for dashboard
        const processedData = {
          metadata: bankData.metadata,
          
          // Sentiment distribution
          sentimentDistribution: {},
          
          // Category counts
          categoryCounts: {},
          
          // Top subcategories
          topSubcategories: [],
          
          // Sentiment by category
          sentimentByCategory: {},
          
          // Named entities
          namedEntities: {},
          
          // NPS Score
          npsScore: 0,
          
          // Recent feedback samples
          recentFeedback: [],
          
          // Add filtered data count for reference
          totalRecords: filteredBankData.length,
          filteredBy: customerType
        };

        // Calculate sentiment distribution
        const sentimentCounts = {};
        filteredBankData.forEach(item => {
          const sentiment = item.sentiment;
          sentimentCounts[sentiment] = (sentimentCounts[sentiment] || 0) + 1;
        });
        processedData.sentimentDistribution = sentimentCounts;

        // Calculate NPS Score
        const promoters = sentimentCounts.Promoter || 0;
        const detractors = sentimentCounts.Detractor || 0;
        const total = filteredBankData.length;
        processedData.npsScore = total > 0 ? Math.round(((promoters / total) - (detractors / total)) * 100) : 0;

        // Calculate category counts
        const categoryCounts = {};
        filteredBankData.forEach(item => {
          if (item.processed_data && item.processed_data.categories) {
            item.processed_data.categories.forEach(category => {
              const cat = category.category;
              categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
            });
          }
        });
        processedData.categoryCounts = categoryCounts;

        // Calculate top subcategories with sentiment breakdown
        const subcategoryCounts = {};
        const subcategorySentiments = {};
        
        filteredBankData.forEach(item => {
          if (item.processed_data && item.processed_data.subcategories) {
            item.processed_data.subcategories.forEach(subcategory => {
              const parentCat = subcategory.category;
              const subCat = subcategory.subcategory;
              const sentiment = subcategory.sentiment;
              const key = `${parentCat}:${subCat}`;
              
              // Count total mentions
              subcategoryCounts[key] = (subcategoryCounts[key] || 0) + 1;
              
              // Track sentiment counts
              if (!subcategorySentiments[key]) {
                subcategorySentiments[key] = { positive: 0, negative: 0, neutral: 0 };
              }
              
              subcategorySentiments[key][sentiment]++;
            });
          }
        });

        // Prepare subcategory data with sentiment breakdowns
        processedData.topSubcategories = Object.entries(subcategoryCounts)
          .map(([name, value]) => {
            const sentiments = subcategorySentiments[name] || { positive: 0, negative: 0, neutral: 0 };
            return {
              name,
              value,
              positive: sentiments.positive || 0,
              negative: sentiments.negative || 0,
              neutral: sentiments.neutral || 0
            };
          })
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);

        // Calculate sentiment by category
        const sentimentByCategory = {};
        filteredBankData.forEach(item => {
          if (item.processed_data && item.processed_data.categories) {
            item.processed_data.categories.forEach(category => {
              const cat = category.category;
              const sentiment = category.sentiment;
              
              if (!sentimentByCategory[cat]) {
                sentimentByCategory[cat] = { positive: 0, negative: 0, neutral: 0 };
              }
              
              sentimentByCategory[cat][sentiment]++;
            });
          }
        });
        processedData.sentimentByCategory = sentimentByCategory;

        // Process named entities
        const namedEntities = {};
        filteredBankData.forEach(item => {
          if (item.processed_data && item.processed_data.named_entities) {
            item.processed_data.named_entities.forEach(entity => {
              const name = entity.entity;
              const type = entity.type;
              const sentiment = entity.sentiment;
              
              if (!namedEntities[type]) {
                namedEntities[type] = {};
              }
              
              if (!namedEntities[type][name]) {
                namedEntities[type][name] = { count: 0, positive: 0, negative: 0, neutral: 0 };
              }
              
              namedEntities[type][name].count++;
              
              if (sentiment === 'positive') namedEntities[type][name].positive++;
              else if (sentiment === 'negative') namedEntities[type][name].negative++;
              else namedEntities[type][name].neutral++;
            });
          }
        });

        // Convert to array format
        const topEntitiesByType = {};
        Object.keys(namedEntities).forEach(type => {
          topEntitiesByType[type] = Object.entries(namedEntities[type])
            .map(([name, stats]) => ({ 
              name, 
              count: stats.count,
              positive: stats.positive,
              negative: stats.negative,
              neutral: stats.neutral
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        });

        processedData.namedEntities = topEntitiesByType;

        // Get sample recent feedback
        processedData.recentFeedback = filteredBankData
          .map(item => {
            const categories = item.processed_data && item.processed_data.categories 
              ? item.processed_data.categories.map(c => c.category) 
              : [];
            
            return {
              id: Math.random().toString(36).substr(2, 9),
              sentiment: item.sentiment,
              text: item.text,
              categories: categories
            };
          })
          .slice(0, 15);

        setData(processedData);
        setLoading(false);
      } catch (error) {
        console.error('Error processing data:', error);
        setLoading(false);
      }
    };

    processData();
  }, [bankData, customerType]); // Add customerType to dependencies

  // Calculate customer type counts from raw data
  const getCustomerTypeCounts = () => {
    if (!bankData || !bankData.data) return { managed: 0, unmanaged: 0, total: 0 };
    
    const counts = { managed: 0, unmanaged: 0, total: bankData.data.length };
    
    bankData.data.forEach(item => {
      if (item.customerType === 'managed') {
        counts.managed++;
      } else if (item.customerType === 'unmanaged') {
        counts.unmanaged++;
      }
    });
    
    return counts;
  };

  // Create filtered bankData object to pass to components that need raw data
  const getFilteredBankData = () => {
    if (!bankData) return null;
    
    let filteredData = bankData.data;
    if (customerType !== 'all') {
      filteredData = bankData.data.filter(item => item.customerType === customerType);
    }
    
    return {
      ...bankData,
      data: filteredData
    };
  };

  const formatCategoryData = () => {
    if (!data) return [];
    
    // Get categories with sentiment counts from sentimentByCategory
    return Object.entries(data.categoryCounts).map(([category, count]) => {
      // Get sentiment breakdown for this category
      const sentiments = data.sentimentByCategory[category] || { positive: 0, negative: 0, neutral: 0 };
      
      return {
        name: category.replace('_', ' '),
        value: count,
        positive: sentiments.positive || 0,
        negative: sentiments.negative || 0,
        neutral: sentiments.neutral || 0
      };
    }).sort((a, b) => b.value - a.value);
  };

  const formatSentimentByCategoryData = () => {
    if (!data) return [];
    
    return Object.entries(data.sentimentByCategory).map(([category, sentiments]) => ({
      name: category.replace('_', ' '),
      positive: sentiments.positive,
      negative: sentiments.negative,
      neutral: sentiments.neutral
    })).sort((a, b) => (b.positive + b.negative + b.neutral) - (a.positive + a.negative + a.neutral));
  };

  // Show loading if data is not ready
  if (!bankData || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <main className="">
        <div className="mb-8">
          <FilterBar 
            activeCategory={activeCategory} 
            setActiveCategory={setActiveCategory}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            categories={bankData.metadata.categories}
            customerType={customerType}
            setCustomerType={setCustomerType}
            customerTypeCounts={getCustomerTypeCounts()}
          />
        </div>
        
        {/* NPS Score and Sentiment Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-1"
          >
            <NPSScore data={data} />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-2"
          >
            <SentimentOverview data={data.sentimentDistribution} />
          </motion.div>
        </div>

        {/* AI Insights */}
        {/* <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
          >
            <AIInsights data={DATA} />
          </motion.div>
        </div> */}
        
        {/* Top Pain Points Analysis */}
        <div className="mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <TopPainPoints data={getFilteredBankData()} />
          </motion.div>
        </div>
        
        
        {/* Category Breakdown and Subcategory Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.65 }}
          >
            <CategoryBreakdown data={formatCategoryData()} />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <SubcategoryAnalysis data={data.topSubcategories} />
          </motion.div>
        </div>
        
        {/* Category Insights */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.75 }}
          >
            <CategoryInsights data={getFilteredBankData()} />
          </motion.div>
        </div>
        
        {/* Sentiment by Category */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <SentimentByCategory data={formatSentimentByCategoryData()} />
          </motion.div>
        </div>
        
        {/* Entity Analysis */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.85 }}
          >
            <EntityAnalysis data={data.namedEntities} />
          </motion.div>
        </div>
        
        
        {/* Recent Feedback Table */}
        {/* <div className="grid grid-cols-1 gap-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <FeedbackTable data={data.recentFeedback} />
          </motion.div>
        </div> */}
      </main>
    </div>
  );
};

export default Dashboard;